import React from 'react';
import { useClickTracking, useFormTracking } from '../../hooks/useAnalytics';

/**
 * Higher-Order Component for automatic click tracking
 * 
 * Wraps components to automatically track click events
 * without requiring manual event handler setup
 */

interface WithClickTrackingProps {
  trackingId?: string;
  trackingType?: string;
  trackingData?: Record<string, any>;
  className?: string;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent) => void;
}

/**
 * HOC factory function that adds click tracking to any component
 */
export function withClickTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  defaultTrackingType: string = 'component'
) {
  return React.forwardRef<any, P & WithClickTrackingProps>((props, ref) => {
    const { trackElementClick } = useClickTracking();
    const { 
      trackingId, 
      trackingType = defaultTrackingType, 
      trackingData, 
      onClick,
      ...restProps 
    } = props;

    const handleClick = (event: React.MouseEvent) => {
      // Track the click
      trackElementClick(event, trackingType, {
        tracking_id: trackingId,
        ...trackingData
      });

      // Call original onClick if provided
      if (onClick) {
        onClick(event);
      }
    };

    return (
      <WrappedComponent
        {...(restProps as P)}
        ref={ref}
        onClick={handleClick}
      />
    );
  });
}

/**
 * Tracked button component
 */
const ButtonComponent = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>((props, ref) => (
  <button {...props} ref={ref} />
));
ButtonComponent.displayName = 'ButtonComponent';

export const TrackedButton = withClickTracking(ButtonComponent, 'button');

/**
 * Tracked link component
 */
const LinkComponent = React.forwardRef<HTMLAnchorElement, React.AnchorHTMLAttributes<HTMLAnchorElement>>((props, ref) => {
  // Ensure the anchor has content for accessibility
  const { children, 'aria-label': ariaLabel, ...rest } = props;
  
  // If there are no children and no aria-label, provide a warning in dev mode
  if (!children && !ariaLabel && process.env.NODE_ENV === 'development') {
    console.warn('TrackedLink component should have children or an aria-label for accessibility');
  }
  
  return <a {...rest} aria-label={ariaLabel} ref={ref}>
    {children || ''}
  </a>;
});
LinkComponent.displayName = 'LinkComponent';

export const TrackedLink = withClickTracking(LinkComponent, 'link');

/**
 * Generic tracked element component
 */
interface TrackedElementProps extends WithClickTrackingProps {
  as?: string;
  href?: string;
  [key: string]: any;
}

export const TrackedElement: React.FC<TrackedElementProps> = ({
  as = 'div',
  trackingId,
  trackingType = 'element',
  trackingData,
  onClick,
  children,
  ...props
}) => {
  const { trackElementClick } = useClickTracking();

  const handleClick = (event: React.MouseEvent) => {
    trackElementClick(event, trackingType, {
      tracking_id: trackingId,
      ...trackingData
    });

    if (onClick) {
      onClick(event);
    }
  };

  return React.createElement(
    as,
    { ...props, onClick: handleClick },
    children
  );
};

/**
 * Form tracking component
 */
interface TrackedFormProps extends React.FormHTMLAttributes<HTMLFormElement> {
  formId: string;
  formName?: string;
  onSubmit?: (event: React.FormEvent) => void;
}

export const TrackedForm: React.FC<TrackedFormProps> = ({
  formId,
  formName,
  onSubmit,
  children,
  ...props
}) => {
  const { trackFormSubmission } = useFormTracking();

  const handleSubmit = (event: React.FormEvent) => {
    trackFormSubmission(formId, formName);

    if (onSubmit) {
      onSubmit(event);
    }
  };

  return (
    <form {...props} onSubmit={handleSubmit}>
      {children}
    </form>
  );
};
